import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from 'components/Accordion';
import Button from 'components/DesignSystem/Button';
import DatePicker from 'components/DatePicker';
import RadioButton from 'components/DesignSystem/RadioButton';
import CalendarioLegenda from 'components/Relatorios/shared/Aprovacao/CalendarioLegenda';
import useCalendarioAprovacao from 'hooks/Relatorios/Aprovacao/useCalendarioAprovacao';
import {
  selectMensalDataAnalisada,
  selectMensalCenario,
  setDataAnalisada,
  setCenario,
} from 'redux/reducers/relatoriosMensalReducer';
import { getAprovacaoConfig } from 'helper/Relatorios/Aprovacao/config';
import { MensalCenario } from 'types/Relatorios/Mensal';

import './index.scss';

const config = getAprovacaoConfig('mensal');

const CENARIOS: { value: MensalCenario; label: string }[] = (
  Object.keys(config.variante.labels) as MensalCenario[]
).map((value) => ({ value, label: config.variante.labels[value] }));

/**
 * Painel de controle do Informe Mensal. Espelha o `SemanalControlPanel` (mesmo
 * padrão do Figma: Accordion do design system + DatePicker inline para a Data
 * Analisada, radios fechados Early/Preliminar/POS_ONS para o Cenário).
 * Rascunho local (useState) só faz commit ao slice no clique em "Atualizar Data".
 *
 * Com a aprovação habilitada, o calendário ganha bolinhas (admin) ou só permite dias
 * publicados (cliente), consultando o **cenário aplicado**. D8: a data do rascunho
 * acompanha a data aplicada; o cenário do rascunho não é ressincronizado.
 */
export default function MensalControlPanel() {
  const dispatch = useDispatch();
  const dataAnalisada = useSelector(selectMensalDataAnalisada);
  const cenarioAplicado = useSelector(selectMensalCenario);

  const [draftData, setDraftData] = useState(
    () => new Date(dataAnalisada.ano, dataAnalisada.mes - 1, dataAnalisada.dia),
  );
  const [draftCenario, setDraftCenario] = useState<MensalCenario>(cenarioAplicado);

  const {
    datePickerProps, mostraLegenda, onDataSelecionada,
  } = useCalendarioAprovacao(config, dataAnalisada, cenarioAplicado);

  const { ano, mes, dia } = dataAnalisada;
  useEffect(() => {
    setDraftData((atual) => (
      atual.getFullYear() === ano && atual.getMonth() === mes - 1 && atual.getDate() === dia
        ? atual
        : new Date(ano, mes - 1, dia)
    ));
  }, [ano, mes, dia]);

  const handleDataChange = useCallback((date: Date | null) => {
    if (!date) return;
    setDraftData(date);
    onDataSelecionada(date);
  }, [onDataSelecionada]);

  const handleCenarioChange = useCallback((value: MensalCenario) => () => {
    setDraftCenario(value);
  }, []);

  const handleAtualizar = useCallback(() => {
    dispatch(setDataAnalisada({
      mes: draftData.getMonth() + 1,
      ano: draftData.getFullYear(),
      dia: draftData.getDate(),
    }));
    dispatch(setCenario(draftCenario));
  }, [dispatch, draftData, draftCenario]);

  return (
    <div className="mensal-control-panel">
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>Data Analisada</Accordion.Header>
          <Accordion.Body className="d-grid">
            <DatePicker
              inline
              selected={draftData}
              onChange={handleDataChange}
              maxDate={new Date()}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...datePickerProps}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>Cenário</Accordion.Header>
          <Accordion.Body className="d-grid gap-1">
            {CENARIOS.map(({ value, label }) => (
              <RadioButton
                key={value}
                name="mensal-cenario"
                label={label}
                value={value}
                checked={draftCenario === value}
                onChange={handleCenarioChange(value)}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Button onClick={handleAtualizar} className="mensal-control-panel__atualizar">
        Atualizar Data
      </Button>

      {mostraLegenda && <CalendarioLegenda isAdmin />}
    </div>
  );
}
